import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes, Navigate, useLocation, useParams } from 'react-router';
import { AppShell } from '../layout/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { NotFoundPage } from '../features/errors/NotFoundPage';
import { ServerErrorPage } from '../features/errors/ServerErrorPage';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPasskeyPage } from '../features/auth/RegisterPasskeyPage';
import { ProfilePage } from '../features/profile/ProfilePage';
import { UsersListPage } from '../features/users/UsersListPage';
import { UserEditPage } from '../features/users/UserEditPage';
import { TurnoverPage } from '../features/turnover/TurnoverPage';
import { ChartsPage } from '../features/charts/ChartsPage';
import { TaxReportsListPage } from '../features/tax-reports/TaxReportsListPage';
import { TaxReportDetailPage } from '../features/tax-reports/TaxReportDetailPage';
import { OrdersListPage } from '../features/orders/OrdersListPage';
import { OrderDetailPage } from '../features/orders/OrderDetailPage';
import { TabsListPage } from '../features/tabs/TabsListPage';
import { TabDetailPage } from '../features/tabs/TabDetailPage';
import { PaymentsListPage } from '../features/payments/PaymentsListPage';
import { RefundsListPage } from '../features/refunds/RefundsListPage';
import { LedgerListPage } from '../features/ledger/LedgerListPage';
import { LedgerDetailPage } from '../features/ledger/LedgerDetailPage';
import { ExactEntriesListPage } from '../features/exact/ExactEntriesListPage';
import { ExactEntryDetailPage } from '../features/exact/ExactEntryDetailPage';
import { ConfigurationPage } from '../features/configuration/ConfigurationPage';
import { ExactRedirectPage } from '../features/configuration/ExactRedirectPage';
import { TerminalsListPage } from '../features/terminals/TerminalsListPage';
import { TerminalEditPage } from '../features/terminals/TerminalEditPage';
import { DevicesListPage } from '../features/devices/DevicesListPage';
import { DeviceEditPage } from '../features/devices/DeviceEditPage';
import { TablesListPage } from '../features/tables/TablesListPage';
import { CategoriesListPage } from '../features/categories/CategoriesListPage';
import { CategoryEditPage } from '../features/categories/CategoryEditPage';
import { ProductsListPage } from '../features/products/ProductsListPage';
import { ProductEditPage } from '../features/products/ProductEditPage';
import { DiscountsListPage } from '../features/discounts/DiscountsListPage';
import { DiscountEditPage } from '../features/discounts/DiscountEditPage';
import { LinksListPage } from '../features/links/LinksListPage';
import { ConsumptionReportPage } from '../features/consumption-report/ConsumptionReportPage';
import { RolesListPage } from '../features/roles/RolesListPage';
import { RoleEditPage } from '../features/roles/RoleEditPage';
import { RecognitionDaySessionsListPage } from '../features/session/RecognitionDaySessionsListPage';
import { OnboardingPage } from '../features/onboarding/OnboardingPage';
import { VoucherRulesListPage } from '../features/loyalty/VoucherRulesListPage';
import { VoucherRuleEditPage } from '../features/loyalty/VoucherRuleEditPage';
import { VouchersListPage } from '../features/loyalty/VouchersListPage';
import { BlueprintsListPage } from '../features/nights/BlueprintsListPage';
import { BlueprintEditPage } from '../features/nights/BlueprintEditPage';
import { NightsPage } from '../features/nights/NightsPage';
import { NightEditPage } from '../features/nights/NightEditPage';
import { EntranceScansPage } from '../features/nights/EntranceScansPage';
import { TicketDetailPage } from '../features/nights/TicketDetailPage';
import { TicketsListPage } from '../features/nights/TicketsListPage';

// Registered only in the Vite dev server. The dynamic import stays inside the
// DEV branch so a production build does not include the page module.
const DevLoginPage = import.meta.env.DEV
  ? lazy(() =>
      import('../features/auth/DevLoginPage').then((module) => ({
        default: module.DevLoginPage,
      })),
    )
  : null;

/**
 * The admin's old blueprint URLs (this entity was called Event): keeps bookmarks working by sending the
 * list and a single blueprint to their /blueprints page, id and query string intact (DEPRECATIONS.md#admin-events-redirects).
 */
function LegacyEventsRedirect() {
  const { id } = useParams<{ id?: string }>();
  const { search } = useLocation();
  return <Navigate to={`/blueprints${id ? `/${id}` : ''}${search}`} replace />;
}

/**
 * The admin's old night URLs (this entity was called Production): sends the calendar and a single night to their
 * /nights page, id and query string intact (DEPRECATIONS.md#admin-productions-redirects).
 */
function LegacyProductionsRedirect() {
  const { id } = useParams<{ id?: string }>();
  const { search } = useLocation();
  return <Navigate to={`/nights${id ? `/${id}` : ''}${search}`} replace />;
}

/**
 * The old filter query params (`?production=`, `?productions=`) of a list page: renames them once and keeps every
 * other param, so bookmarks and shared links still filter (DEPRECATIONS.md#admin-production-query-params).
 */
function LegacyQueryParams({ renames, children }: { renames: Record<string, string>; children: ReactNode }) {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const old = Object.keys(renames).filter((key) => params.has(key));
  if (old.length === 0) return children;
  for (const key of old) {
    const value = params.get(key)!;
    params.delete(key);
    if (!params.has(renames[key]!)) params.set(renames[key]!, value);
  }
  return <Navigate to={`${pathname}?${params.toString()}`} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {DevLoginPage ? (
        <Route
          path="/dev-login"
          element={
            <Suspense fallback={null}>
              <DevLoginPage />
            </Suspense>
          }
        />
      ) : null}
      <Route path="/register-passkey/:token" element={<RegisterPasskeyPage />} />
      <Route path="/500" element={<ServerErrorPage />} />

      <Route element={<AppShell />}>
        <Route
          index
          element={
            <ProtectedRoute permission={['report:turnover:read', 'report:turnover:current_week:read']}>
              <TurnoverPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="charts"
          element={
            <ProtectedRoute permission="report:chart:read">
              <LegacyQueryParams renames={{ productions: 'nights' }}>
                <ChartsPage />
              </LegacyQueryParams>
            </ProtectedRoute>
          }
        />
        <Route
          path="consumption-report"
          element={
            <ProtectedRoute permission="report:consumption:read">
              <ConsumptionReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="recognition-day-sessions"
          element={
            <ProtectedRoute>
              <RecognitionDaySessionsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tax-reports"
          element={
            <ProtectedRoute permission="report:tax:read">
              <TaxReportsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tax-reports/day/:date"
          element={
            <ProtectedRoute permission="report:tax:read">
              <TaxReportDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tax-reports/month/:year/:month"
          element={
            <ProtectedRoute permission="report:tax:read">
              <TaxReportDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="orders"
          element={
            <ProtectedRoute permission="order:read">
              <OrdersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="orders/:id"
          element={
            <ProtectedRoute permission="order:read">
              <OrderDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tabs"
          element={
            <ProtectedRoute permission="tab:read">
              <TabsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tabs/:id"
          element={
            <ProtectedRoute permission="tab:read">
              <TabDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="payments"
          element={
            <ProtectedRoute permission="payment:read">
              <PaymentsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="refunds"
          element={
            <ProtectedRoute permission="refund:read">
              <RefundsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ledger"
          element={
            <ProtectedRoute permission="ledger:read">
              <LedgerListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="ledger/:id"
          element={
            <ProtectedRoute permission="ledger:read">
              <LedgerDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="exact"
          element={
            <ProtectedRoute permission="exact:configure">
              <ExactEntriesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="exact/redirect"
          element={
            <ProtectedRoute permission="exact:configure">
              <ExactRedirectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="exact/:id"
          element={
            <ProtectedRoute permission="exact:configure">
              <ExactEntryDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="configuration"
          element={
            <ProtectedRoute
              permission={[
                'exact:configure',
                'customerio:configure',
                'ticket:configure',
                'loyalty:read',
                'scope:manage',
              ]}
            >
              <ConfigurationPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="products"
          element={
            <ProtectedRoute permission="product:read">
              <ProductsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="products/new"
          element={
            <ProtectedRoute permission="product:read">
              <ProductEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="products/:id"
          element={
            <ProtectedRoute permission="product:read">
              <ProductEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="categories"
          element={
            <ProtectedRoute permission="category:read">
              <CategoriesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="categories/:id"
          element={
            <ProtectedRoute permission="category:read">
              <CategoryEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="discounts"
          element={
            <ProtectedRoute permission="discount:read">
              <DiscountsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="discounts/new"
          element={
            <ProtectedRoute permission="discount:read">
              <DiscountEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="discounts/:id"
          element={
            <ProtectedRoute permission="discount:read">
              <DiscountEditPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="terminals"
          element={
            <ProtectedRoute permission="terminal:read">
              <TerminalsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="terminals/new"
          element={
            <ProtectedRoute permission="terminal:read">
              <TerminalEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="terminals/:id"
          element={
            <ProtectedRoute permission="terminal:read">
              <TerminalEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="devices"
          element={
            <ProtectedRoute permission="device:read">
              <DevicesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="devices/:id"
          element={
            <ProtectedRoute permission="device:read">
              <DeviceEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tables"
          element={
            <ProtectedRoute permission="table:read">
              <TablesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="links"
          element={
            <ProtectedRoute permission="link:read">
              <LinksListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="roles"
          element={
            <ProtectedRoute permission="role:manage">
              <RolesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="roles/new"
          element={
            <ProtectedRoute permission="role:manage">
              <RoleEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="roles/:id"
          element={
            <ProtectedRoute permission="role:manage">
              <RoleEditPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="users"
          element={
            <ProtectedRoute permission="user:read">
              <UsersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/new"
          element={
            <ProtectedRoute permission="user:read">
              <UserEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id"
          element={
            <ProtectedRoute permission="user:read">
              <UserEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="voucher-rules"
          element={
            <ProtectedRoute permission="voucher:read">
              <VoucherRulesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="voucher-rules/new"
          element={
            <ProtectedRoute permission="voucher:manage">
              <VoucherRuleEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="voucher-rules/:id"
          element={
            <ProtectedRoute permission="voucher:read">
              <VoucherRuleEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vouchers"
          element={
            <ProtectedRoute permission="voucher:read">
              <VouchersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="loyalty-settings"
          element={<Navigate to="/configuration?tab=loyalty" replace />}
        />
        <Route
          path="blueprints"
          element={
            <ProtectedRoute permission="blueprint:read">
              <BlueprintsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="blueprints/new"
          element={
            <ProtectedRoute permission="blueprint:manage">
              <BlueprintEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="blueprints/:id"
          element={
            <ProtectedRoute permission="blueprint:read">
              <BlueprintEditPage />
            </ProtectedRoute>
          }
        />
        <Route path="events" element={<LegacyEventsRedirect />} />
        <Route path="events/:id" element={<LegacyEventsRedirect />} />
        <Route path="productions" element={<LegacyProductionsRedirect />} />
        <Route path="productions/:id" element={<LegacyProductionsRedirect />} />
        <Route
          path="nights"
          element={
            <ProtectedRoute permission="night:read">
              <NightsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="nights/:id"
          element={
            <ProtectedRoute permission="night:read">
              <NightEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tickets"
          element={
            <ProtectedRoute permission="ticket:read">
              <LegacyQueryParams renames={{ production: 'night' }}>
                <TicketsListPage />
              </LegacyQueryParams>
            </ProtectedRoute>
          }
        />
        <Route
          path="tickets/:id"
          element={
            <ProtectedRoute permission="ticket:read">
              <TicketDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="entrance-scans"
          element={
            <ProtectedRoute permission="entrance:read">
              <LegacyQueryParams renames={{ production: 'night' }}>
                <EntranceScansPage />
              </LegacyQueryParams>
            </ProtectedRoute>
          }
        />
        <Route
          path="refused-tickets"
          element={<Navigate to="/entrance-scans" replace />}
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
